import { useEffect, useState } from "react";
import { IoIosArrowRoundBack, IoIosArrowRoundForward } from "react-icons/io";
import LoadingSpinner from "../../components/elements/LoadingSpinner";
import { fetchStateUsersWithOrders } from "../../redux/thunk";
import { useLocation } from "react-router-dom";
import Pagination from "../../components/Pagination";

interface OrderUser {
  id: string;
  firstName: string;
  lastName: string;
  middleName: string;
  email: string;
  categoryType: string;
  orderCount: number;
}

const StateUsersWithOrders = () => {
  const [searchedUser, setSearchedUser] = useState("");
  const [orderUsers, setOrderUsers] = useState<OrderUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalResults, setTotalResults] = useState(0);
  const itemsPerPage = 10;

  const location = useLocation();
  const initialOrderUsers = location.state?.orderUsers || [];

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      await fetchStateUsersWithOrders(currentPage, itemsPerPage).then(
        (response) => {
          setLoading(false);
          setOrderUsers(response?.results ?? []);
          setCurrentPage(response.currentPage);
          setTotalPages(response.totalPages);
          setTotalResults(response.totalResults);
        }
      );
    };
    fetchData();
  }, [currentPage]);

  // Pagination
  const prevPage = () => {
    if (currentPage > 1) {
      goToPage(currentPage - 1);
    }
  };

  const nextPage = () => {
    if (currentPage < totalPages) {
      goToPage(currentPage + 1);
    }
  };

  // Generate page numbers for pagination
  const goToPage = async (pageNumber: any) => {
    setSearchedUser("");
    setCurrentPage(pageNumber);
  };

  const indexOfLastPost = currentPage * itemsPerPage;
  const indexOfFirstPost = indexOfLastPost - itemsPerPage;

  const mainData = orderUsers.length > 0 ? orderUsers : initialOrderUsers;

  return (
    <section className="mx-auto p-6 bg-white rounded-lg shadow">
      <header className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">
            State Users with Orders
          </h1>
        </div>
        <div className="mt-4 md:mt-0">
          <input
            type="text"
            placeholder="Search by name or email..."
            value={searchedUser}
            onChange={(e) => setSearchedUser(e.target.value)}
            className="border rounded px-3 py-2 text-sm w-64"
          />
        </div>
      </header>
      <div className="overflow-x-auto rounded-lg border">
        <table className="min-w-full bg-white">
          <thead>
            <tr className="bg-gray-100">
              <th className="py-3 px-4 text-left font-semibold text-gray-700">
                S/N
              </th>
              <th className="py-3 px-4 text-left font-semibold text-gray-700">
                Full Name
              </th>
              <th className="py-3 px-4 text-left font-semibold text-gray-700">
                Email
              </th>
              <th className="py-3 px-4 text-left font-semibold text-gray-700">
                Category
              </th>
              <th className="py-3 px-4 text-left font-semibold text-gray-700">
                Order Count
              </th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={4} className="text-center py-8">
                  <LoadingSpinner />
                </td>
              </tr>
            ) : mainData.length > 0 ? (
              mainData
                .filter(
                  (user: {
                    firstName: string;
                    lastName: string;
                    email: string;
                  }) =>
                    user.firstName
                      .toLowerCase()
                      .includes(searchedUser.toLowerCase()) ||
                    user.lastName
                      .toLowerCase()
                      .includes(searchedUser.toLowerCase()) ||
                    user.email
                      .toLowerCase()
                      .includes(searchedUser.toLowerCase())
                )
                .map((user: OrderUser, index: number) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="py-3 px-4">
                      {index + indexOfFirstPost + 1}
                    </td>
                    <td className="py-3 px-4">
                      {user.firstName} {user.middleName} {user.lastName}
                    </td>
                    <td className="py-3 px-4">{user.email}</td>
                    <td className="py-3 px-4">{user.categoryType}</td>
                    <td className="py-3 px-4">{user.orderCount}</td>
                  </tr>
                ))
            ) : (
              <tr>
                <td colSpan={4} className="text-center py-8 text-gray-500">
                  No users found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <section className="py-3">
        <Pagination
          length={totalResults}
          currentPage={currentPage}
          responseTotalPages={totalPages}
          handlePagination={goToPage}
          nextPage={nextPage}
          prevPage={prevPage}
          itemsPerPage={itemsPerPage}
        />
      </section>
    </section>
  );
};

export default StateUsersWithOrders;
